import { Inject, Injectable, Optional } from "@angular/core";
import { ConfigBase, ConfigQuery, ConfigService as GlobalConfigService, TenantBase } from "@obg/global";
import {
	cloneDeep,
	forEach,
	get,
	has,
	isArray,
	isFunction,
	isNil,
	isObject,
	isString,
	merge,
	set,
	toNumber,
	unset,
} from "lodash-es";

import { CONFIG_SERVICE_ENABLE_PREPROCESSOR_INJECTION_TOKEN, GLOBAL_CONFIG } from "../const";
import { GlobalConfig } from "../model";
import { toBoolean } from "../utils/conversions/type-conversion";

@Injectable({
	providedIn: "root",
})
export class ConfigService<C extends TenantBase<C> = any> extends GlobalConfigService<C> {
	private config: Record<string, any> = {};

	constructor(
		@Optional() @Inject(GLOBAL_CONFIG) globalConfig?: GlobalConfig<C>,
		@Optional() @Inject(CONFIG_SERVICE_ENABLE_PREPROCESSOR_INJECTION_TOKEN) enablePreprocessor = false
	) {
		super(
			globalConfig?.configBase ?? ({} as ConfigBase<C>),
			globalConfig?.jurisdiction ?? "",
			globalConfig?.dimensions ?? [],
			globalConfig?.tenant ?? "",
			enablePreprocessor
		);

		if (globalConfig?.config) {
			this.setItems(globalConfig.config);
		}

		if (globalConfig?.configBase) {
			this.updateConfig(globalConfig.configBase);
		}
	}

	getOrDefault<T>(path: string, defaultValue: T): T {
		return this.hasConfig(path) ? this.get<T>(path) : defaultValue;
	}

	get<T>(path: string): T;

	get<T>(path: ConfigQuery<C, T | undefined>): T;

	get<T>(path: ConfigQuery<C, T | undefined>, defaultValue: T): T;

	get<T>(path?: string | ConfigQuery<C, T | undefined>, defaultValue?: T): T {
		if (!isString(path)) {
			return super.get<T>(path as ConfigQuery<C, T>, defaultValue);
		}

		if (!path) {
			return this.config as T;
		}

		const value = get(this.config, path);

		if (!isNil(value)) {
			return value as T;
		}

		throw new Error(`Could not retrieve config from '${path}'`);
	}

	set(config: Record<string, any>) {
		if (!config) {
			throw new Error("Config object parameter is null or undefined");
		}

		this.config = cloneDeep(config);
	}

	setItem<T>(path: string, config: T) {
		if (isNil(config) || isFunction(config)) {
			throw new Error("Configuration must be defined");
		}

		const value = get(this.config, path);

		if (!isNil(value) && isObject(value) && !isArray(value)) {
			merge(value, config);
		} else {
			set(this.config, path, config);
		}
	}

	removeItem(path: string) {
		unset(this.config, path);
	}

	setItems(configs: Record<string, any>) {
		forEach(configs, (value, key) => {
			this.setItem(key, value);
		});
	}

	getStringOrDefault<T extends string | undefined>(path: string, defaultValue: T): string | T {
		return this.hasConfig(path) ? this.getString(path) : defaultValue;
	}

	getString(path: string): string {
		let value = this.get<string>(path);

		value = isObject(value) ? JSON.stringify(value) : value.toString();

		if (!isString(value)) {
			throw new Error(`Config '${path}' is not a string. Value is '${value}'.`);
		}

		return value;
	}

	getNumberOrDefault<T extends number | undefined>(path: string, defaultValue: T): number | T {
		return this.hasConfig(path) ? this.getNumber(path) : defaultValue;
	}

	getNumber(path: string): number {
		let value = this.get<number | string>(path);
		value = toNumber(value);

		if (isNaN(value)) {
			throw new Error(`Config '${path}' is not a number. Value is '${value}'.`);
		}

		return value;
	}

	getBooleanOrDefault<T extends boolean | undefined>(path: string, defaultValue: T): boolean | T {
		return this.hasConfig(path) ? this.getBoolean(path) : defaultValue;
	}

	getBoolean(path: string): boolean {
		let value = this.get<boolean | string | number | undefined>(path);
		value = toBoolean(value);

		if (isNil(value)) {
			throw new Error(`Config '${path}' is not a boolean. Value is '${value}'.`);
		}

		return value;
	}

	private hasConfig(path: string): boolean {
		if (!this.config) {
			throw new Error("Config is undefined or null. Make sure that the service has been initialized.");
		}

		return has(this.config, path);
	}
}
